import type { AdminLocale } from '../../content/locale.js';
import { ApiError } from '../../lib/http.js';

/* Mensagens pt-BR/en-US para erros de descontos negociados (ADR-0349). Validação do billing
   (base inexistente, override sem desconto, validFrom velho) chega como `not_blocked`.

   `locale` é parâmetro OPCIONAL (default 'pt-BR') e não um `Bilingual<T>`/`useBilingual`: segue
   o mesmo precedente de `pack-catalog-labels.ts` — a função é chamada fora de árvore de render
   (callback síncrono de `onError` de mutation), então um parâmetro simples é mais direto que
   subir contexto React até aqui. Os consumidores desta área (que já leem `useAdminLocale`)
   passam o idioma corrente; sem argumento, cai no default. */
function discountErrorMessage(code: ApiError['code'], locale: AdminLocale): string {
  if (locale === 'en-US') {
    switch (code) {
      case 'not_found':
        return 'Negotiated discount not found.';
      case 'not_blocked':
        return 'Invalid data (base plan, discount, or validity window).';
      case 'network':
        return 'Connection failed. Try again.';
      default:
        return 'Operation failed. Try again.';
    }
  }
  switch (code) {
    case 'not_found':
      return 'Desconto negociado não encontrado.';
    case 'not_blocked':
      return 'Dados inválidos (plano base, desconto ou janela de validade).';
    case 'network':
      return 'Falha de conexão. Tente novamente.';
    default:
      return 'Operação falhou. Tente novamente.';
  }
}

export function planErrorMessage(err: unknown, locale: AdminLocale = 'pt-BR'): string {
  if (err instanceof ApiError) return discountErrorMessage(err.code, locale);
  return locale === 'en-US' ? 'Unexpected error.' : 'Erro inesperado.';
}

/* Mensagens pt-BR/en-US para o CRUD do catálogo de planos (ADR-0414/0419) — distinto dos
   descontos negociados acima. `conflict`/`plan_key_taken` = lookup key já tem plano ativo;
   `invalid` = corpo inválido (tier/key/preço/capabilities).

   Mesmo padrão de `locale?` opcional: esta função é importada também por
   `routes/plan-create-page.tsx` e `routes/targeted-plan-create-page.tsx` — duas frentes de
   migração próprias que ainda chamam com um único argumento. O parâmetro opcional mantém essas
   chamadas compilando sem mudança; passar `locale` explícito é responsabilidade do call site. */
function catalogErrorMessage(err: ApiError, locale: AdminLocale): string {
  const code = err.code;
  if (locale === 'en-US') {
    switch (err.serverCode) {
      case 'invalid_validity_window':
        return 'The end date must be after the start date.';
      case 'plan_active_subscription_requires_migration':
        return 'This change requires a migration because the plan has an active contract.';
      case 'plan_contract_valid_from_immutable':
        return 'The start date cannot change after a contract has been linked to this plan.';
      case 'plan_included_seats_below_active':
        return 'Included seats cannot be lower than the seats currently in use.';
      case 'plan_concurrent_update':
        return 'This plan changed while you were editing it. Review the latest data and try again.';
    }
    switch (code) {
      case 'not_found':
        return 'Plan not found.';
      case 'plan_key_taken':
        return 'An active plan with this lookup key already exists. Archive the current one before recreating.';
      case 'invalid':
        return 'Invalid plan data. Check tier, lookup key, price, and capabilities.';
      case 'network':
        return 'Connection failed. Try again.';
      default:
        return 'Could not save the plan. Try again.';
    }
  }
  switch (err.serverCode) {
    case 'invalid_validity_window':
      return 'A data final deve ser posterior à data inicial.';
    case 'plan_active_subscription_requires_migration':
      return 'Esta alteração exige uma migração porque o plano possui um contrato ativo.';
    case 'plan_contract_valid_from_immutable':
      return 'A data inicial não pode mudar depois que um contrato foi vinculado a este plano.';
    case 'plan_included_seats_below_active':
      return 'Os assentos inclusos não podem ficar abaixo dos assentos atualmente em uso.';
    case 'plan_concurrent_update':
      return 'Este plano mudou durante a edição. Revise os dados atuais e tente novamente.';
  }
  switch (code) {
    case 'not_found':
      return 'Plano não encontrado.';
    case 'plan_key_taken':
      return 'Já existe um plano ativo com essa lookup key. Arquive o atual antes de recriar.';
    case 'invalid':
      return 'Dados do plano inválidos. Verifique tier, lookup key, preço e capabilities.';
    case 'network':
      return 'Falha de conexão. Tente novamente.';
    default:
      return 'Não foi possível salvar o plano. Tente novamente.';
  }
}

export function planCatalogErrorMessage(err: unknown, locale: AdminLocale = 'pt-BR'): string {
  if (err instanceof ApiError) return catalogErrorMessage(err, locale);
  return locale === 'en-US' ? 'Unexpected error.' : 'Erro inesperado.';
}
